import React, { useState, useCallback } from 'react';
import { Table, Typography, Card, Tag, Button, Progress } from 'antd';
import { 
  SyncOutlined, 
  EyeOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
} from '@ant-design/icons';
import { adminTasksApi } from '../../services/api/adminTasks';
import { AdminTask, AdminTaskStatus, AdminTaskType } from '../../types';
import { useApi } from '../../hooks';
import { formatDate } from '../../utils';
import TaskDetailDrawer from '../../components/admin/tasks/TaskDetailDrawer';

const { Title } = Typography;

const TaskMonitoringPage: React.FC = () => {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);

  const fetchTasks = useCallback(() => {
    return adminTasksApi.getTasks({
      pageIndex,
      pageSize,
    });
  }, [pageIndex, pageSize]);

  const { data, loading, refresh } = useApi(fetchTasks, { immediate: true });

  const handleViewDetails = (taskId: string) => {
    setSelectedTaskId(taskId);
    setIsDrawerVisible(true);
  };

  const getStatusTag = (status: AdminTaskStatus) => {
    switch (status) {
      case AdminTaskStatus.COMPLETED:
        return <Tag color="success" icon={<CheckCircleOutlined />}>Hoàn thành</Tag>;
      case AdminTaskStatus.IN_PROGRESS:
        return <Tag color="processing" icon={<SyncOutlined spin />}>Đang chạy</Tag>;
      case AdminTaskStatus.FAILED:
        return <Tag color="error" icon={<CloseCircleOutlined />}>Thất bại</Tag>;
      case AdminTaskStatus.PENDING:
        return <Tag color="default" icon={<ClockCircleOutlined />}>Chờ xử lý</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns = [
    {
      title: 'Ngày khởi tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Loại Task',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type: AdminTaskType) => {
        if (type === AdminTaskType.FULL_AUTO_CHAPTER_TO_VIDEO) {
          return <Tag color="blue">Full Pipeline</Tag>;
        }
        if (type === AdminTaskType.AUTO_CHAPTER) {
          return <Tag color="green">Auto Chapter</Tag>;
        }
        if (type === AdminTaskType.AUTO_PROCESS) {
          return <Tag color="orange">Auto Process</Tag>;
        }
        if (type === AdminTaskType.AUTO_EPISODE) {
          return <Tag color="magenta">Auto Episode</Tag>;
        }
        if (type === AdminTaskType.AUTO_CLONE_PROCESS) {
          return <Tag color="purple">Auto Clone Process</Tag>;
        }
        return <Tag>{type}</Tag>;
      }
    },
    {
      title: 'Truyện',
      key: 'storyName',
      render: (record: AdminTask) => {
        const input = (record.detail as any)?.input;
        if (!input) return '-';
        return (
          <div>
            <b>{input.storyName || 'N/A'}</b>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>ID: {input.sourceStoryId}</div>
          </div>
        );
      },
    },
    {
      title: 'Chapter',
      key: 'chapterName',
      render: (record: AdminTask) => {
        const input = (record.detail as any)?.input;
        if (!input) return '-';
        return input.chapterName || input.chapterId || '-';
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: AdminTaskStatus) => getStatusTag(status),
    },
    {
      title: 'Tiến độ',
      key: 'progress',
      width: 200,
      render: (record: AdminTask) => (
        <Progress 
          percent={record.detail.progress} 
          size="small" 
          status={record.status === AdminTaskStatus.FAILED ? 'exception' : 'active'} 
        />
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      render: (record: AdminTask) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />} 
          onClick={() => handleViewDetails(record.id)}
        >
          Chi tiết
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>Quản lý Tiến trình Tự động</Title>
        <Button icon={<SyncOutlined />} onClick={refresh} loading={loading}>
          Làm mới danh sách
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.data || []}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pageIndex + 1,
            pageSize: pageSize,
            total: data?.totalRecord || 0,
            onChange: (page) => setPageIndex(page - 1),
          }}
        />
      </Card>

      <TaskDetailDrawer
        visible={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
        taskId={selectedTaskId}
      />
    </div>
  );
};

export default TaskMonitoringPage;
